// Per-dialect synthesizer for CREATE / ALTER / DROP ROLE (v2 Slice F1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice F1.
//
// Per-engine surface:
//   - mssql:    CREATE ROLE name [AUTHORIZATION owner]
//               ALTER ROLE name {ADD MEMBER | DROP MEMBER | WITH NAME = ...}
//               DROP ROLE [IF EXISTS] name
//   - postgres: CREATE ROLE name [WITH <attrs>]   (roles double as users)
//               ALTER ROLE name {RENAME TO | WITH PASSWORD | VALID UNTIL}
//               DROP ROLE [IF EXISTS] name
//   - mysql:    CREATE ROLE [IF NOT EXISTS] 'name'[@'host']
//               ALTER ROLE not commonly used → rejected (use op:'grant')
//               DROP ROLE [IF EXISTS] 'name'[@'host']
//   - oracle:   CREATE ROLE name [{IDENTIFIED BY pwd | NOT IDENTIFIED}]
//               ALTER ROLE name {IDENTIFIED BY pwd | NOT IDENTIFIED}
//               DROP ROLE name
//   - sqlite:   not supported (no auth model).

import type { SqlEngine } from '../../../sql-grammar/model';
import type { RoleChange, RoleDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';

const SQLITE_HINT = `sqlite has no auth model. Connection-level access is OS / file-permission driven.`;

function rejectIfSqlite(engine: SqlEngine, op: string): void {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${SQLITE_HINT}`,
    );
  }
}

function rejectField(engine: SqlEngine, op: string, field: string, allowed: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} ${op} does not accept '${field}' — only ${allowed} accept this field.`,
  );
}

function mysqlPrincipal(name: string, host: string | undefined): string {
  return `'${name}'@'${host ?? '%'}'`;
}

// ─── CREATE ROLE ──────────────────────────────────────────────────────────

export function synthesizeCreateRole(engine: SqlEngine, role: RoleDef): string {
  rejectIfSqlite(engine, 'create-role');
  if (engine === 'mssql') return synthMssqlCreateRole(role);
  if (engine === 'postgres') return synthPgsqlCreateRole(role);
  if (engine === 'mysql') return synthMysqlCreateRole(role);
  return synthOracleCreateRole(role);
}

function synthMssqlCreateRole(role: RoleDef): string {
  if (hasAnyPgsql(role)) rejectField('mssql', 'create-role', 'pgsql*', 'pgsql');
  if (role.mysqlHost !== undefined || role.ifNotExists !== undefined) {
    rejectField('mssql', 'create-role', 'mysql*', 'mysql');
  }
  if (role.oracleIdentifiedBy !== undefined || role.oracleNotIdentified !== undefined) {
    rejectField('mssql', 'create-role', 'oracle*', 'oracle');
  }
  const name = maybeQuoteIdent('mssql', role.name, role.quoted ?? false);
  let s = `CREATE ROLE ${name}`;
  if (role.mssqlAuthorization !== undefined) s += ` AUTHORIZATION ${role.mssqlAuthorization}`;
  return s;
}

function synthPgsqlCreateRole(role: RoleDef): string {
  if (role.mssqlAuthorization !== undefined) {
    rejectField('postgres', 'create-role', 'mssqlAuthorization', 'mssql');
  }
  if (role.mysqlHost !== undefined || role.ifNotExists !== undefined) {
    rejectField('postgres', 'create-role', 'mysql*', 'mysql');
  }
  if (role.oracleIdentifiedBy !== undefined || role.oracleNotIdentified !== undefined) {
    rejectField('postgres', 'create-role', 'oracle*', 'oracle');
  }
  if (role.pgsqlPassword !== undefined && role.pgsqlLogin !== true) {
    throw new Error(
      `applyDdlEdit: postgres create-role 'pgsqlPassword' requires `
        + `'pgsqlLogin: true' — passwords are meaningful only on login-capable roles.`,
    );
  }
  const name = maybeQuoteIdent('postgres', role.name, role.quoted ?? false);
  let s = `CREATE ROLE ${name}`;
  const attrs = pgsqlRoleAttrs(role);
  if (attrs.length > 0) s += ` WITH ${attrs.join(' ')}`;
  return s;
}

function pgsqlRoleAttrs(role: RoleDef): string[] {
  const out: string[] = [];
  if (role.pgsqlLogin !== undefined) out.push(role.pgsqlLogin ? 'LOGIN' : 'NOLOGIN');
  if (role.pgsqlSuperuser !== undefined) out.push(role.pgsqlSuperuser ? 'SUPERUSER' : 'NOSUPERUSER');
  if (role.pgsqlCreateDb !== undefined) out.push(role.pgsqlCreateDb ? 'CREATEDB' : 'NOCREATEDB');
  if (role.pgsqlCreateRole !== undefined) {
    out.push(role.pgsqlCreateRole ? 'CREATEROLE' : 'NOCREATEROLE');
  }
  if (role.pgsqlInherit !== undefined) out.push(role.pgsqlInherit ? 'INHERIT' : 'NOINHERIT');
  if (role.pgsqlReplication !== undefined) {
    out.push(role.pgsqlReplication ? 'REPLICATION' : 'NOREPLICATION');
  }
  if (role.pgsqlPassword !== undefined) out.push(`PASSWORD '${role.pgsqlPassword}'`);
  if (role.pgsqlValidUntilText !== undefined) out.push(`VALID UNTIL '${role.pgsqlValidUntilText}'`);
  return out;
}

function synthMysqlCreateRole(role: RoleDef): string {
  if (role.mssqlAuthorization !== undefined) {
    rejectField('mysql', 'create-role', 'mssqlAuthorization', 'mssql');
  }
  if (hasAnyPgsql(role)) rejectField('mysql', 'create-role', 'pgsql*', 'pgsql');
  if (role.oracleIdentifiedBy !== undefined || role.oracleNotIdentified !== undefined) {
    rejectField('mysql', 'create-role', 'oracle*', 'oracle');
  }
  let s = 'CREATE ROLE';
  if (role.ifNotExists) s += ' IF NOT EXISTS';
  s += ` ${mysqlPrincipal(role.name, role.mysqlHost)}`;
  return s;
}

function synthOracleCreateRole(role: RoleDef): string {
  if (role.mssqlAuthorization !== undefined) {
    rejectField('oracle', 'create-role', 'mssqlAuthorization', 'mssql');
  }
  if (hasAnyPgsql(role)) rejectField('oracle', 'create-role', 'pgsql*', 'pgsql');
  if (role.mysqlHost !== undefined || role.ifNotExists !== undefined) {
    rejectField('oracle', 'create-role', 'mysql*', 'mysql');
  }
  if (role.oracleIdentifiedBy !== undefined && role.oracleNotIdentified) {
    throw new Error(
      `applyDdlEdit: oracle create-role cannot set both 'oracleIdentifiedBy' `
        + `and 'oracleNotIdentified' — they are mutually exclusive auth kinds.`,
    );
  }
  const name = maybeQuoteIdent('oracle', role.name, role.quoted ?? false);
  let s = `CREATE ROLE ${name}`;
  if (role.oracleIdentifiedBy !== undefined) s += ` IDENTIFIED BY ${role.oracleIdentifiedBy}`;
  else if (role.oracleNotIdentified) s += ' NOT IDENTIFIED';
  return s;
}

function hasAnyPgsql(role: RoleDef): boolean {
  return role.pgsqlLogin !== undefined || role.pgsqlPassword !== undefined
    || role.pgsqlSuperuser !== undefined || role.pgsqlCreateDb !== undefined
    || role.pgsqlCreateRole !== undefined || role.pgsqlInherit !== undefined
    || role.pgsqlReplication !== undefined || role.pgsqlValidUntilText !== undefined;
}

// ─── ALTER ROLE ───────────────────────────────────────────────────────────

export function synthesizeAlterRole(
  engine: SqlEngine,
  roleName: string,
  quoted: boolean,
  _mysqlHost: string | undefined,
  change: RoleChange,
): string {
  rejectIfSqlite(engine, 'alter-role');
  const empty = change.mssqlAddMember === undefined && change.mssqlDropMember === undefined
    && change.mssqlName === undefined && change.pgsqlRenameTo === undefined
    && change.pgsqlPassword === undefined && change.pgsqlValidUntilText === undefined
    && change.oracleIdentifiedBy === undefined && change.oracleNotIdentified === undefined;
  if (empty) {
    throw new Error(`applyDdlEdit: alter-role requires at least one change field.`);
  }
  // mysql ALTER ROLE is rare in practice and the surface is intentionally
  // narrow on every other engine — we redirect mysql callers to op:'grant'
  // / op:'revoke' for membership management. mysqlHost is therefore unused
  // here (it is still carried on alter-role for future extensions).
  if (engine === 'mysql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mysql does not support alter-role — mysql ALTER ROLE is `
        + `not a commonly-used statement. Use op:'grant' / op:'revoke' to `
        + `manage role membership, or DROP + CREATE to rotate the role.`,
    );
  }
  if (engine === 'mssql') return synthMssqlAlterRole(roleName, quoted, change);
  if (engine === 'postgres') return synthPgsqlAlterRole(roleName, quoted, change);
  return synthOracleAlterRole(roleName, quoted, change);
}

function synthMssqlAlterRole(roleName: string, quoted: boolean, change: RoleChange): string {
  if (change.pgsqlRenameTo !== undefined || change.pgsqlPassword !== undefined
      || change.pgsqlValidUntilText !== undefined) {
    rejectField('mssql', 'alter-role', 'pgsql*', 'pgsql');
  }
  if (change.oracleIdentifiedBy !== undefined || change.oracleNotIdentified !== undefined) {
    rejectField('mssql', 'alter-role', 'oracle*', 'oracle');
  }
  const name = maybeQuoteIdent('mssql', roleName, quoted);
  if (change.mssqlAddMember !== undefined) {
    return `ALTER ROLE ${name} ADD MEMBER ${change.mssqlAddMember}`;
  }
  if (change.mssqlDropMember !== undefined) {
    return `ALTER ROLE ${name} DROP MEMBER ${change.mssqlDropMember}`;
  }
  // mssqlName
  return `ALTER ROLE ${name} WITH NAME = ${change.mssqlName}`;
}

function synthPgsqlAlterRole(roleName: string, quoted: boolean, change: RoleChange): string {
  if (change.mssqlAddMember !== undefined || change.mssqlDropMember !== undefined
      || change.mssqlName !== undefined) {
    rejectField('postgres', 'alter-role', 'mssql*', 'mssql');
  }
  if (change.oracleIdentifiedBy !== undefined || change.oracleNotIdentified !== undefined) {
    rejectField('postgres', 'alter-role', 'oracle*', 'oracle');
  }
  const name = maybeQuoteIdent('postgres', roleName, quoted);
  if (change.pgsqlRenameTo !== undefined) {
    return `ALTER ROLE ${name} RENAME TO ${change.pgsqlRenameTo}`;
  }
  if (change.pgsqlPassword !== undefined) {
    return `ALTER ROLE ${name} WITH PASSWORD '${change.pgsqlPassword}'`;
  }
  // pgsqlValidUntilText
  return `ALTER ROLE ${name} VALID UNTIL '${change.pgsqlValidUntilText}'`;
}

function synthOracleAlterRole(roleName: string, quoted: boolean, change: RoleChange): string {
  if (change.mssqlAddMember !== undefined || change.mssqlDropMember !== undefined
      || change.mssqlName !== undefined) {
    rejectField('oracle', 'alter-role', 'mssql*', 'mssql');
  }
  if (change.pgsqlRenameTo !== undefined || change.pgsqlPassword !== undefined
      || change.pgsqlValidUntilText !== undefined) {
    rejectField('oracle', 'alter-role', 'pgsql*', 'pgsql');
  }
  const name = maybeQuoteIdent('oracle', roleName, quoted);
  if (change.oracleIdentifiedBy !== undefined) {
    return `ALTER ROLE ${name} IDENTIFIED BY ${change.oracleIdentifiedBy}`;
  }
  // oracleNotIdentified
  return `ALTER ROLE ${name} NOT IDENTIFIED`;
}

// ─── DROP ROLE ────────────────────────────────────────────────────────────

export function synthesizeDropRole(
  engine: SqlEngine,
  roleName: string,
  quoted: boolean,
  ifExists: boolean,
  mysqlHost: string | undefined,
): string {
  rejectIfSqlite(engine, 'drop-role');
  if (engine === 'mysql') {
    let s = 'DROP ROLE';
    if (ifExists) s += ' IF EXISTS';
    s += ` ${mysqlPrincipal(roleName, mysqlHost)}`;
    return s;
  }
  const name = maybeQuoteIdent(engine, roleName, quoted);
  let s = 'DROP ROLE';
  if (ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  return s;
}
